import { Input } from "@/common/components/ui/input";
import { Label } from "@/common/components/ui/label";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/common/components/ui/card";
import { TrendingUp } from "lucide-react";
import { TabProps } from "../types";

const PerformanceTab = ({
  formData,
  handleInputChange,
  validationErrors = {},
  readOnly = false,
}: TabProps) => {
  const readOnlyClass = readOnly ? "bg-slate-50 cursor-not-allowed" : "";
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <CardTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
            <TrendingUp className="h-4 w-4 text-primary" />
            Valuation Ratios
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-x-8 gap-y-6 p-6">
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              P/E Ratio
            </Label>
            <Input
              name="pe_ratio"
              type="number"
              step="0.01"
              value={formData.pe_ratio}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.pe_ratio || "0.00"}
              className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                validationErrors.pe_ratio
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
            {validationErrors.pe_ratio && (
              <p className="text-xs text-red-600">
                {validationErrors.pe_ratio}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              P/BV Ratio
            </Label>
            <Input
              name="pbv_ratio"
              type="number"
              step="0.01"
              disabled={readOnly}
              value={formData.pbv_ratio}
              onChange={handleInputChange}
              className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              ROE (%)
            </Label>
            <Input
              name="roe"
              type="number"
              step="0.01"
              value={formData.roe}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.roe || "0.00"}
              min={0}
              max={100}
              className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                validationErrors.roe
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
            {validationErrors.roe && (
              <p className="text-xs text-red-600">{validationErrors.roe}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              ROCE (%)
            </Label>
            <Input
              name="roce"
              type="number"
              step="0.01"
              value={formData.roce}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.roce || "0.00"}
              min={0}
              max={100}
              className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                validationErrors.roce
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
            {validationErrors.roce && (
              <p className="text-xs text-red-600">{validationErrors.roce}</p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              PAT Margin (%)
            </Label>
            <Input
              name="pat_margin"
              type="number"
              step="0.01"
              value={formData.pat_margin}
              onChange={handleInputChange}
              disabled={readOnly}
              placeholder={formData.pat_margin || "0.00"}
              min={0}
              max={100}
              className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass} ${
                validationErrors.pat_margin
                  ? "border-red-300 focus:border-red-500"
                  : ""
              }`}
            />
            {validationErrors.pat_margin && (
              <p className="text-xs text-red-600">
                {validationErrors.pat_margin}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              Debt/Equity Ratio
            </Label>
            <Input
              name="debt_equity"
              type="number"
              step="0.01"
              value={formData.debt_equity}
              onChange={handleInputChange}
              disabled={readOnly}
              className={`h-10 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
            />
          </div>
          <div className="col-span-2 space-y-1.5">
            <Label className="text-[10px] font-bold uppercase text-slate-400">
              EPS (Pre-Issue)
            </Label>
            <Input
              name="eps_pre"
              type="number"
              step="0.01"
              value={formData.eps_pre}
              onChange={handleInputChange}
              disabled={readOnly}
              className={`h-10 font-medium placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 ${readOnlyClass}`}
            />
          </div>
        </CardContent>
      </Card>

      {/* <Card className="border-slate-200 shadow-sm">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 px-6 py-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-sm font-bold text-slate-800">
              <Globe className="h-4 w-4 text-orange-500" />
              Grey Market & Gains
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          <div className="grid grid-cols-3 gap-6">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                GMP (₹)
              </Label>
              <Input
                name="gmp"
                type="number"
                value={formData.gmp}
                disabled={readOnly}
                onChange={handleInputChange}
                className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-10 border-orange-100 bg-orange-50/30 font-bold ${readOnlyClass}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Exp. Listing (₹)
              </Label>
              <Input
                name="expected_listing"
                type="number"
                step="0.01"
                value={formData.expected_listing}
                disabled={readOnly}
                onChange={handleInputChange}
                className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-10 border-orange-100 bg-orange-50/30 font-bold ${readOnlyClass}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Gain (%)
              </Label>
              <Input
                name="listing_gain"
                type="number"
                step="0.1"
                value={formData.listing_gain}
                onChange={handleInputChange}
                disabled={readOnly}
                className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-10 border-orange-100 bg-orange-50/30 font-bold ${readOnlyClass}`}
              />
            </div>
          </div>
          <Separator />
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Subscription (Total)
              </Label>
              <Input
                name="subscription_total"
                type="number"
                step="0.01"
                value={formData.subscription_total}
                onChange={handleInputChange}
                disabled={readOnly}
                className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-10 bg-slate-50 font-bold ${readOnlyClass}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-bold uppercase text-slate-400">
                Retail Portion (x)
              </Label>
              <Input
                name="subscription_retail"
                type="number"
                step="0.01"
                value={formData.subscription_retail}
                disabled={readOnly}
                onChange={handleInputChange}
                className={`placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 h-10 bg-slate-50 font-bold ${readOnlyClass}`}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 rounded-xl bg-slate-900 p-4 text-white shadow-lg">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-3 w-3 text-emerald-400" />
              <span className="text-[9px] font-bold uppercase tracking-widest text-slate-400">
                Market Sentiment
              </span>
            </div>
            <p className="text-[11px] font-medium leading-relaxed text-slate-300">
              GMP and Listing Gain are estimates based on secondary market data.
            </p>
          </div>
        </CardContent>
      </Card> */}
    </div>
  );
};

export default PerformanceTab;
